/**
 * Repository Implementation: Auth
 * Implements IAuthRepository interface
 */

import { inject, injectable } from "inversify";
import type { IAuthRepository } from "../../domain/repository/IAuthRepository";
import type { LoginCredentials, LoginResult, LoginUserData } from "../../domain/model/AuthModel";
import { AuthenticationError } from "../../domain/model/AuthModel";
import { AuthApiService } from "../remote/service/AuthApiService";

const AUTH_TOKEN_KEY = "bearerToken";
const AUTH_TOKEN_EXPIRY_KEY = "bearerTokenExpiry";
const AUTH_REFRESH_TOKEN_KEY = "refreshToken";
const AUTH_USER_DATA_KEY = "authUserData";

@injectable()
export class AuthRepositoryImpl implements IAuthRepository {
  @inject(AuthApiService)
  private readonly service!: AuthApiService;

  async login(credentials: LoginCredentials): Promise<LoginResult> {
    const response = await this.service.login(credentials);
    const { accessToken, refreshToken, expiresIn, user } = response.data;
    this.storeTokens(accessToken, refreshToken, expiresIn);
    this.storeUserData(user);
    return { accessToken, user };
  }

  getStoredToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  getStoredTokenExpiry(): number | null {
    const expiry = localStorage.getItem(AUTH_TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  }

  isTokenExpired(): boolean {
    const expiry = this.getStoredTokenExpiry();
    if (!expiry) return true;
    // Add 30 second buffer before actual expiry
    return Date.now() >= expiry - 30000;
  }

  isAuthenticated(): boolean {
    const token = this.getStoredToken();
    return token !== null && !this.isTokenExpired();
  }

  storeTokens(accessToken: string, refreshToken: string, expiresInSeconds: number): void {
    const expiryTimestamp = Date.now() + expiresInSeconds * 1000;
    localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
    localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(AUTH_TOKEN_EXPIRY_KEY, expiryTimestamp.toString());
  }

  getStoredRefreshToken(): string | null {
    return localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  }

  clearToken(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_TOKEN_EXPIRY_KEY);
    localStorage.removeItem(AUTH_USER_DATA_KEY);
  }

  storeUserData(user: LoginUserData): void {
    localStorage.setItem(AUTH_USER_DATA_KEY, JSON.stringify(user));
  }

  getStoredUserData(): LoginUserData | null {
    const userData = localStorage.getItem(AUTH_USER_DATA_KEY);
    if (!userData) return null;
    try {
      return JSON.parse(userData) as LoginUserData;
    } catch {
      return null;
    }
  }

  async ensureValidToken(): Promise<string> {
    const storedToken = this.getStoredToken();

    if (storedToken && !this.isTokenExpired()) {
      return storedToken;
    }

    // Token missing or expired - clear token and throw error
    this.clearToken();
    throw new AuthenticationError("Session expired. Please log in again.");
  }
}

